const convertHTMLObjToArrayForKpp = (htmlObj) => {
  return Object.entries(htmlObj).map(([key, html]) => {
    return { id: key, ...parseHTMLStringForKpp(html) };
  });
}

// Build-time (Node) equivalent of decoding the HTML entities left over from extracting
// these values out of an HTML fragment — no `document` available here, so a manual map
// for the entities that can realistically show up in a business name/address.
const decodeHtmlEntitiesNode = (str) => {
  if (!str) return str;
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'");
}

const parseHTMLStringForKpp = (htmlString) => {
  const name = decodeHtmlEntitiesNode((htmlString.match(/<[^>]+data-id="name"[^>]*>([^<]+)<\/[^>]+>/) || [])[1] ?? '');
  const description = decodeHtmlEntitiesNode((htmlString.match(/<[^>]+data-id="description"[^>]*>\s*([^<]+)/s) || [])[1]?.trim() ?? '');
  const rating = (htmlString.match(/<[^>]+data-id="ratingValue"[^>]*>([^<]+)<\/[^>]+>/) || [])[1] ?? '';
  const reviewCount = (htmlString.match(/<[^>]+data-id="reviewCount"[^>]*>([^<]+)<\/[^>]+>/) || [])[1] ?? '';
  const address = decodeHtmlEntitiesNode((htmlString.match(/<[^>]+data-id="address"[^>]*>([^<]+)<\/[^>]+>/) || [])[1] ?? '');
  const image = (htmlString.match(/<[^>]+srcset="([^"]+)"[^>]*>/) || [])[1] ?? '';

  return { name, description, rating, reviewCount, address, image };
}

// Structured data requires absolute URLs; card fragments store the image path relative to the site root.
const absolutizeUrl = (url, domain) => {
  if (!url) return undefined;
  return /^https?:\/\//.test(url) ? url : `${domain}${url}`;
}

const generateJsonLdForKpp = (kppData, site, placeName = '', domain = '') => {
  return kppData.map(item => {
    const aggregateRating = item.rating ? {
      "@type": "AggregateRating",
      ratingValue: item.rating,
      reviewCount: item.reviewCount
    } : undefined;

    const address = {
      "@type": "PostalAddress",
      addressLocality: item.address || placeName,
      addressCountry: "US"
    };

    const logoUrl = absolutizeUrl(item.image, domain);
    const logo = logoUrl ? {
      "@type": "ImageObject",
      contentUrl: logoUrl
    } : undefined;

    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      additionalType: site === 'demolition' ? "https://schema.org/HomeAndConstructionBusiness" : "https://schema.org/WasteManagement",
      name: item.name,
      description: item.description,
      ...(aggregateRating && { aggregateRating }),
      address,
      ...(logo && { logo })
    };
  });
};

const generateTypicalPricesSchema = (typicalPricesChart, placeName) => {
  if (!typicalPricesChart?.rows?.length) return null

  const { rows, city_name } = typicalPricesChart
  const displayCity = city_name || placeName || 'your area'

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `Dumpster Rental in ${displayCity}`,
    "areaServed": {
      "@type": "City",
      "name": displayCity
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Dumpster Rental Pricing",
      "itemListElement": rows.map(row => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": `${row.size_label} dumpster`
        },
        "priceSpecification": {
          "@type": "PriceSpecification",
          "minPrice": Math.round(row.low_price),
          "maxPrice": Math.round(row.high_price),
          "priceCurrency": "USD"
        }
      }))
    }
  }
}

// Shared LocalBusiness block nested under each Offer's seller (Product schema, PPP pages).
const buildPppSeller = (pro, domain) => {
  const rating = (pro.rating && pro.reviewCount) ? {
    "@type": "AggregateRating",
    ratingValue: pro.rating,
    reviewCount: pro.reviewCount,
  } : undefined;
  const image = absolutizeUrl(pro.image, domain);

  return {
    "@type": "LocalBusiness",
    name: pro.name,
    ...(image && { image }),
    ...(rating && { aggregateRating: rating }),
  };
}

const generateJsonLdForPpp = (pppData, pppPrices, buildMode, placeName = '', site = 'dumpster', domain = '') => {
  const EXACT_SIZES = { ppp10: 10, ppp20: 20, ppprolloff: 30 }
  const exactSize = EXACT_SIZES[buildMode] || null
  const sizeLabel = exactSize ? `${exactSize}-yard` : 'dumpster'

  const exactPricesPerPro = []
  const allExactPrices = []
  const nearbySizeMap = {}

  pppData.forEach(pro => {
    const services = pppPrices[String(pro.id)] || []
    const proExactPrices = []

    services.forEach(s => {
      if (s.size === exactSize) {
        const price = parseFloat(s.price)
        proExactPrices.push(price)
        allExactPrices.push(price)
      } else if (s.size !== null) {
        if (!nearbySizeMap[s.size]) nearbySizeMap[s.size] = []
        nearbySizeMap[s.size].push(parseFloat(s.price))
      }
    })

    if (proExactPrices.length > 0) {
      exactPricesPerPro.push({ pro, price: Math.min(...proExactPrices) })
    }
  })

  const hasExactPrices = exactPricesPerPro.length > 0
  const hasNearbyPrices = Object.keys(nearbySizeMap).length > 0

  const ratedPros = pppData.filter(p => p.rating && p.reviewCount)
  const totalReviews = ratedPros.reduce((sum, p) => sum + Number(p.reviewCount), 0)
  const weightedRating = totalReviews > 0
    ? ratedPros.reduce((sum, p) => sum + Number(p.rating) * Number(p.reviewCount), 0) / totalReviews
    : null
  const aggregateRating = weightedRating ? {
    "@type": "AggregateRating",
    ratingValue: weightedRating.toFixed(1),
    reviewCount: totalReviews,
  } : undefined

  const productName = exactSize
    ? `${exactSize} Yard Dumpster Rental in ${placeName}`
    : `Dumpster Rental in ${placeName}`

  // Generic site image (same asset already used for og:image/twitter:image) — Google only requires
  // a real, accessible image on the Product, not a photo of the specific size.
  const genericImage = domain
    ? `${domain}/seo/${site === 'demolition' ? 'hometown-demolition-contractors-logo.png' : 'hometown-dumpster-rental-logo.png'}`
    : undefined

  const isRelatedTo = Object.entries(nearbySizeMap).map(([size, prices]) => ({
    "@type": "Product",
    name: `${size} Yard Dumpster Rental in ${placeName}`,
    offers: {
      "@type": "AggregateOffer",
      lowPrice: Math.min(...prices).toFixed(2),
      highPrice: Math.max(...prices).toFixed(2),
      offerCount: prices.length,
      priceCurrency: "USD",
    }
  }))

  // Shared by Cases B and C: no confirmed price for this exact size, one quote-only Offer per pro.
  const buildQuoteOnlyOffers = () => pppData.map(pro => ({
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    seller: buildPppSeller(pro, domain),
  }))

  // Case A: exact-size prices available → Product + AggregateOffer
  if (hasExactPrices) {
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: productName,
      description: `Compare ${sizeLabel} dumpster rental options and starting prices from local providers in ${placeName}.`,
      ...(genericImage && { image: genericImage }),
      ...(aggregateRating && { aggregateRating }),
      offers: {
        "@type": "AggregateOffer",
        lowPrice: Math.min(...allExactPrices).toFixed(2),
        highPrice: Math.max(...allExactPrices).toFixed(2),
        offerCount: exactPricesPerPro.length,
        priceCurrency: "USD",
        offers: exactPricesPerPro.map(({ pro, price }) => ({
          "@type": "Offer",
          seller: buildPppSeller(pro, domain),
          price: price.toFixed(2),
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
        })),
      },
      ...(isRelatedTo.length > 0 && { isRelatedTo }),
    }
  }

  // Case B: no exact prices but nearby sizes have prices → quote-only + isRelatedTo.
  // Typed as Service (not Product) so Google's Product-only validators (Product Snippets /
  // Merchant Listing, which require a price on every Offer) simply don't evaluate this node.
  // No root-level aggregateRating here: Service isn't a valid itemReviewed parent per Google's
  // Review Snippet spec (unlike Product/LocalBusiness) — each seller already carries its own
  // aggregateRating on a LocalBusiness node via buildPppSeller, which stays valid.
  if (hasNearbyPrices) {
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      name: productName,
      description: `Contact local providers in ${placeName} for a ${sizeLabel} dumpster price quote based on your debris type and project needs.`,
      ...(genericImage && { image: genericImage }),
      offers: buildQuoteOnlyOffers(),
      ...(isRelatedTo.length > 0 && { isRelatedTo }),
    }
  }

  // Case C: no prices at all → one Offer per pro, all InStock, no prices. Same Service typing as Case B.
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: productName,
    description: `Find available ${sizeLabel} dumpster rentals in ${placeName}. Upfront rates are currently unlisted for this market — contact our featured local haulers directly for a custom price quote.`,
    ...(genericImage && { image: genericImage }),
    offers: buildQuoteOnlyOffers(),
  }
}

const generateJsonLdForPppConstruction = (pppData, pppPrices, placeName = '') => {
  const [cityName, addressRegion] = placeName.split(', ')

  // For each size, track the pro with the lowest price
  const sizeMap = {}

  pppData.forEach(pro => {
    const services = pppPrices[String(pro.id)] || []
    services.forEach(s => {
      if (s.size === null) return
      const price = parseFloat(s.price)
      if (!sizeMap[s.size] || price < sizeMap[s.size].price) {
        sizeMap[s.size] = { proName: pro.name, price }
      }
    })
  })

  const sortedSizes = Object.keys(sizeMap).map(Number).sort((a, b) => a - b)

  const itemListElement = sortedSizes.map(size => {
    const { proName, price } = sizeMap[size]
    const hasPrice = !isNaN(price) && price > 0
    return {
      "@type": "Offer",
      name: `${size}-Yard Construction Dumpster Rental`,
      ...(hasPrice && { price: price.toFixed(2), priceCurrency: "USD" }),
      availability: "https://schema.org/InStock",
      businessFunction: "http://purl.org/goodrelations/v1#LeaseOut",
      itemOffered: {
        "@type": "Service",
        name: `${size}-Yard Dumpster for Construction Debris`,
      },
      offeredBy: {
        "@type": "LocalBusiness",
        name: proName,
      },
    }
  })

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Construction Dumpster Rental in ${placeName}`,
    description: `Compare construction dumpster rental options from local providers in ${cityName || placeName}. Available sizes may include 10-yard, 20-yard, and 30-yard dumpsters depending on the provider and debris type.`,
    serviceType: "Construction Dumpster Rental",
    areaServed: {
      "@type": "City",
      name: cityName || placeName,
      address: {
        "@type": "PostalAddress",
        ...(addressRegion && { addressRegion }),
        addressCountry: "US",
      },
    },
    ...(itemListElement.length > 0 && {
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Construction Dumpster Rental Options",
        itemListElement,
      },
    }),
  }
}


// then we made this function dynamic for dumpster and demolition, when mark has the demolition schema
const generateJsonLdForHome = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Hometown Dumpster Rental",
  "alternateName": "Hometown Local",
  "url": "https://www.hometowndumpsterrental.com/",
  "logo": "https://www.hometowndumpsterrental.com/images/logo.png",
  "description": "Founded in 2008, Hometown Dumpster Rental is a nationwide marketplace making it easy to compare prices and book local dumpster rental and junk removal services.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "2153 Wealthy St., Suite 117",
    "addressLocality": "Grand Rapids",
    "addressRegion": "MI",
    "postalCode": "49506",
    "addressCountry": "US"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1-877-994-6645",
    "contactType": "customer service",
    "areaServed": "US",
    "availableLanguage": "en"
  },
  "sameAs": [
    "https://www.facebook.com/HometownDumpsterRental",
    "https://x.com/hometowndumpstr",
"https://www.bbb.org/us/mi/grand-rapids/profile/dumpster-rentals/hometown-dumpster-rental-0372-90065857",
    "https://www.trustpilot.com/review/www.hometowndumpsterrental.com",
"https://www.youtube.com/c/HometownDumpsterRental"
  ],
  "brand": {
    "@type": "Brand",
    "name": "Hometown Dumpster Rental™",
    "slogan": "Low Prices, Easy Booking."
  }
});

const formatBookingJson = (data, placeId = null) => {
  let newFormData = data
  if (typeof data === 'string') {
    try {
      newFormData = JSON.parse(data);
    } catch (error) {
      console.error('Error parsing JSON:', error);
    }
  }
  const initialValues = [
    { key: 'wasteType', value: [+newFormData.wasteType] },
    { key: 'deliveryAddress', value: (newFormData.odf_street && newFormData.odf_street2) ? newFormData.odf_street + ', ' + newFormData.odf_street2 : ''},
    { key: 'fullFormData', value: newFormData},
  ]
  localStorage.setItem('onlineBookingData',JSON.stringify({ placeId: placeId, baseServiceId: "62a9f2cd61706a135071a7e3", skip: 0, limit: 21, initialValues: initialValues, finalDataForBooking: null }))
  // Let other already-mounted components on the same page (e.g. the KPP/PPP sidebar search,
  // which only reads onlineBookingData once at mount) know the address/waste type changed
  // elsewhere (e.g. the "Get Pricing & Availability" popup), so they can refresh without
  // needing a full page reload.
  if (typeof document !== 'undefined') {
    document.dispatchEvent(new CustomEvent('onlineBookingData:updated', { detail: newFormData }))
  }
  return newFormData;
}

// Build breadcrumb schema Json-LD
const buildBreadcrumbSchema = (page) => {
  const breadcrumb = page.breadcrumb || [];

  // Only include links with both url and label
  const validLinks = breadcrumb.filter(
    link => link?.url && link?.label
  );

  if (validLinks.length === 0) return null;

  const items = validLinks.map((link, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: link.label,
    item: link.url
  }));

  return {
    "@type": "BreadcrumbList",
    itemListElement: items
  };
};

// Build profiles schema Json-LD
const buildWasteManagementServiceSchema = (page, reviewsData = []) => {
  if (!page?.profile) return null;

  const profile = page.profile;

  const schema = {
    "@type": "HomeAndConstructionBusiness",
    "@id": `${page.env?.domain || ''}/${page.url}#business`,
    name: profile.name,
    url: `${page.env?.domain || ''}/${page.url}`,
  };

  // Phone
  if (profile.phone) {
    schema.telephone = profile.phone;
  }

  const SERVICE_PRIORITY = [
        'Dumpster Rental',
        'Junk Removal',
        'Commercial Trash Service',
    ];

  const getPrimaryService = (page) => {
      const services = page.services?.[0] || [];

      const serviceNames = services.map(s => s.name);
      return SERVICE_PRIORITY.find(p => serviceNames.includes(p)) || 'Dumpster Rental';
  };

  const getSeoCities = (page, limit = 3) => {
        const cities = page.areas?.flatMap(area => area.places || []) || [];
        
        return cities.slice(0, limit).map(city => city.split(',')[0]);
    };

  // Description
  if (profile.name && page.services?.length > 0 && page.areas?.length > 0) {
    const service = getPrimaryService(page);
    const cities = getSeoCities(page).join(', ');

    switch (service) {
      case 'Junk Removal':
        schema.description = `Get affordable cleanouts and junk pickup from ${profile.name}. Full-service junk removal in ${cities} and nearby.`;
        break;

      case 'Commercial Trash Service':
        schema.description = `Get affordable commercial trash pickup from ${profile.name} in ${cities} and nearby. Get a quote and save on monthly rates.`;
        break;

      case 'Dumpster Rental':
      default:
        schema.description = `Get dumpster rental pricing and availability for ${profile.name}. Easy online booking & same day/next day delivery to ${cities} and nearby.`;
        break;
    }
  }

  // Logo url
  if (profile.logo?.path) {
    schema.image = profile.logo.path;
  }

  // Address
  if (profile.add_c && profile.add_av && profile.zip) {
    schema.address = {
      "@type": "PostalAddress",
      addressLocality: profile.add_c,
      addressRegion: profile.add_av,
      postalCode: profile.zip,
      addressCountry: "US",
    };

    if (!profile.hide_address && profile.address) {
      schema.address.streetAddress = profile.address;
    }
  }

  // Area Served 
  if (page.areas?.length > 0) {
    const cities = [];

    page.areas.forEach(area => {
      area.places?.forEach(place => {
        const city = place.split(',')[0]?.trim();
        if (city && cities.length < 10) {
          cities.push({
            "@type": "City",
            name: city,
          });
        }
      });
    });

    if (cities.length > 0) {
      schema.areaServed = cities;
    }
  } 
  

  // Offer Catalog (Dumpster Services)
  if (page.serviceSeekerData?.defaultServices?.length > 0) {

    const buildServiceDescription = (service) => {
      const weightDetail = service.details?.find(d => d.text1?.includes('ton'));
      const dayDetail = service.details?.find(d => d.text1?.includes('day'));

      const weightTerm = weightDetail?.text3 || weightDetail?.text1?.split('. <br>')[0];
      const dayTerm = dayDetail?.text3 || dayDetail?.text1?.split('. <br>')[0];

      if (!weightTerm || !dayTerm) return '';

      return `Includes up to ${dayTerm.replace('included', '').trim()} and ${weightTerm.replace('included', '').trim()} of waste`;
    };


    schema.hasOfferCatalog = {
      "@type": "OfferCatalog",
      name: "Waste Management Services",
      itemListElement: [
        {
          "@type": "OfferCatalog",
          name: "Dumpster Rental",
          itemListElement: page.serviceSeekerData.defaultServices.map(service => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: service.title + ' Rental',
              description: buildServiceDescription(service)
            },
            priceSpecification: {
              "@type": "PriceSpecification",
              price: service.price,
              priceCurrency: "USD",
              description: "Starting at price"
            }
          }))
        }
      ]
    };
  }


  // Aggregate Rating
  if (reviewsData?.ratingValue && reviewsData?.reviewCount) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: reviewsData.ratingValue,
      reviewCount: reviewsData.reviewCount,
      bestRating: "5",
      worstRating: "1",
    };
  }

  // Reviews (top N)
  if (reviewsData?.reviews?.length > 0) {
    schema.review = reviewsData.reviews.slice(0, 5);
  }

  return schema;
};



const manualGoogleEvents = (eventName) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: eventName
    });
  }
}

const formatData = (data) => {
  let address = data.find(element => element.key === 'fullFormData').value
  if(address) {
    address = JSON.parse(address)
    let addressObj = {}
    address.forEach(e => addressObj[e[0]] = e[1])
    const wasteType = data.find(element => element.key === 'wasteType').value
    const dumpsterSize = data.find(e => e.key === 'dumpsterSize')?.value
    const obj = {
      "odf_country": addressObj.country,
      "odf_locality": addressObj.locality,
      "odf_place_id": addressObj.place_id,
      "odf_postal_code": addressObj.postal_code,
      "odf_state": addressObj.state,
      "odf_street": addressObj.street,
      "odf_street2": addressObj.street2,
      "wasteType": wasteType,
    }
    if (dumpsterSize) {
      obj.dumpsterSize = dumpsterSize;
    }
    return obj;
  }
}

const getFormDataForStorage = () => {
  let obj = {};
  if(typeof window !== 'undefined'){ 
    const localData = JSON.parse(localStorage.getItem('onlineBookingData'));
    if(localData){
      let populatedData = {};
      if (localData.finalDataForBooking) {
        try {
          populatedData = formatData(localData.finalDataForBooking);
        } catch {}
        // Si finalDataForBooking no tiene datos válidos, caemos a initialValues
        if (!populatedData?.wasteType || !populatedData?.odf_postal_code) {
          populatedData = localData.initialValues?.find(data => data.key === 'fullFormData')?.value;
        }
      } else {
        populatedData = localData.initialValues?.find(data => data.key === 'fullFormData')?.value
      }
      if (populatedData?.wasteType && populatedData?.odf_postal_code) {
        const wasteTypeValue = Array.isArray(populatedData.wasteType) ? populatedData.wasteType[0] : populatedData.wasteType;
        obj.wasteType = wasteTypeValue;
        delete populatedData.wasteType;
        delete populatedData.dumpsterSize;
        obj.addressData = { ...populatedData };
      }
    }
  }
  return obj;
}

const getInitialFormValues = () => {
  const localData = getFormDataForStorage();
  if (localData?.addressData && localData?.wasteType) {
    const allData = {
      ...localData.addressData,
      wasteType: localData.wasteType
    };
    const keyValueArray = Object.entries(allData).map(([key, value]) => ({
      key,
      value
    }));
    return keyValueArray;
  }
  return [];
}

const trackProCardsViews = (eventPrefix, className, startCounter = 0, customerView, sponsored) => {
  if (!window.dataLayer) {
    console.error("dataLayer is not available.");
    return;
  }

  const elements = document.getElementsByClassName(className);
  if (!elements || elements.length === 0) {
    return;
  }
  const viewedServiceIds = new Set(); // track each unique service card
  const viewedProIds = new Set();     // track unique pros for customer views

  if (startCounter > 0) {
    [...elements].slice(0, startCounter).forEach(el => {
      const proId = el.dataset.proid;
      if (proId) viewedProIds.add(proId);
    });
  }

  let counter = startCounter;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const proId = el.dataset.proid;
          const serviceId = el.dataset.serviceid;
          // main event — fire once per serviceId (each card)
          if (!viewedServiceIds.has(serviceId)) {
            viewedServiceIds.add(serviceId);
            counter++;

            const eventName = eventPrefix + counter;
            const eventData = {
              event: eventName,
              proid: proId,
            };
            eventData[eventName] = proId;

            window.dataLayer.push(eventData);
          }

          // customer view — fire once per proId
          if (customerView && !viewedProIds.has(proId)) {
            viewedProIds.add(proId);
            const foundIndex = viewedProIds.size;
            const eventGtaName = (sponsored ? "sponsoredProServiceVisible" : "proServiceVisible") + foundIndex;
            const eventGtaData = {
              event: eventGtaName,
              proid: proId,
            };
            eventGtaData[eventGtaName] = proId;

            window.dataLayer.push(eventGtaData);
          }

          obs.unobserve(el); // stop tracking once it has been seen
        }
      });
    },
    {
      root: null,
      threshold: 0.3, // 30% visible to trigger
    }
  );

  const elementsToProcess = [...elements].slice(startCounter);
  elementsToProcess.forEach(el => observer.observe(el));
}

const trackSimpleEvent = (eventName, proId) => {
  window.dataLayer = window.dataLayer || [];
  
  window.dataLayer.push({
    event: eventName,
    proid: String(proId),
    timestamp: Date.now(),
  });
};


const trackProCardsViewsOnViewport = (eventPrefix, className, startCounter = 0) => {
  if (!window.dataLayer) {
    console.error("dataLayer is not available.");
    return;
  }

  const elements = document.getElementsByClassName(className);
  if (!elements?.length) return;

  const viewed = new Set();
  let counter = startCounter;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const proId = el.dataset.proid;
          if (!viewed.has(proId)) {
            viewed.add(proId);
            counter++;

            const eventName = eventPrefix + counter;
            const eventData = {
              event: eventName,
              proid: proId,
            };
            eventData[eventName] = proId;

            window.dataLayer.push(eventData);
          }

          obs.unobserve(el);
        }
      });
    },
    {
      root: null,
      threshold: 0.3, // 30% visible
    }
  );

  const elementsToProcess = [...elements].slice(startCounter)
  if (elementsToProcess?.length > 0) {
    elementsToProcess.forEach(el => observer.observe(el));
  }
};

const trackBookingSubmission = (proId) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: 'bookingSubmission',
      proid: proId,
      'bookingSubmission': proId
    });
  }
}

const createAddressEvent = async (addressObject, proId) => {
  try {
    if (process.env.GATSBY_EVENTS_API_URL) {
      const source = window.location.origin;
      const url = window.location.href;
      let sessionId = localStorage.getItem('sessionId');
      if (!sessionId) {
        const auxCode = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2)
        localStorage.setItem('sessionId', auxCode)
        sessionId = auxCode;
      }
      const event = {
        eventType: "SC",
        eventDescription: !addressObject.hasNoCity ? "Address Selected" : "No city selected",
        eventData: {
          source,
          sessionId,
          url,
          ...addressObject,
        }
      }
      if (proId) {
        event.proId = proId;
      }

      const response = await fetch(
        `${process.env.GATSBY_EVENTS_API_URL}/events`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(event),
        }
      );
      return await response.json();
    }
    return;
  } catch (error) {
    console.error('Error creating address event:', error);
  }
}

// Extracts numeric size bounds from a chart row's size_label, e.g. "6 - 11 yard" -> [6, 11],
// "30 yard" -> [30, 30]. Shared by the KPP opening paragraph (src/templates/dumpsters-kpp-pages.js)
// and the KPP FAQ tiers below — both need a numeric size range out of a Typical Prices Chart row.
const parseSizeRange = (label) => {
  const nums = (label?.match(/\d+/g) || []).map(Number)
  if (!nums.length) return [null, null]
  return [Math.min(...nums), Math.max(...nums)]
}

const fmtDollars = (value) => (value === null || value === undefined || isNaN(value)) ? null : `$${Math.round(value)}`

// Tier 4 (zero/unusable pricing data) content — its own function because it's also the safe
// fallback for tiers 1-3 below when a "usable" bucketCount still yields an invalid computed
// value (missing avg_* fields, unparseable size_label, etc.) — see the guards in getKppFaqItems.
const buildTier4FaqItems = (cityShort) => [
  {
    question: `How much does it cost to rent a dumpster in ${cityShort}?`,
    answer: `Dumpster rental rates in ${cityShort} vary based on several factors, including container size, total debris weight, length of rental, and local landfill tipping fees. To get accurate, up-to-date pricing for your project, request pricing and availability from haulers serving the ${cityShort} area.`
  },
  {
    question: `What dumpster sizes are available for rent in ${cityShort}?`,
    answer: `The most common roll-off dumpster sizes available are: 10-Yard Dumpster: Best for small cleanouts, heavy materials (dirt/brick), and minor remodels. 20-Yard Dumpster: Most popular choice for kitchen remodels, roof replacements, and medium cleanouts. 30-Yard Dumpster: Ideal for whole-home cleanouts, large renovations, and construction. 40-Yard Dumpster: Designed for commercial projects, massive demolitions, or major builds.`
  },
  {
    question: `How long is a standard dumpster rental period?`,
    answer: `Most dumpster companies include a standard 7 day rental period, though 3 day rentals and 14 day rentals are also common. If you complete your project early, you can schedule an early pickup, or you can extend your rental for a flat daily rate if you need more time.`
  },
  {
    question: `How do weight limits work with dumpster rentals?`,
    answer: `Each dumpster size comes with an included weight limit measured in tons. When your dumpster is picked up, it is weighed at a local waste facility. If the total weight exceeds your included limit, an overage charge per extra ton will apply.`
  },
  {
    question: `Do I need a permit for a dumpster rental in ${cityShort}?`,
    answer: `Permits are generally not required if the container sits on private property (like a driveway). However, if you plan to place the dumpster on a public street, sidewalk, or right-of-way in ${cityShort}, you may need to obtain a temporary street permit from the city public works department.`
  },
  {
    question: `What materials are not allowed in a dumpster?`,
    answer: `To comply with environmental rules, local waste haulers prohibit items like hazardous chemicals, wet paint, gasoline, asbestos, propane tanks, batteries, and medical waste. Tires and electronics may require special handling fees depending on local regulations.`
  }
]

// Builds the 4-tier FAQ content for Dumpster Rental KPP pages (per stakeholder design doc,
// confirmed 11/08/2026 — copy is literal, only bracketed placeholders below are substituted).
// Same bucketCount discriminant as getKppOpeningParagraph: 0 rows -> Tier 4, 1 row -> Tier 3,
// 2 rows -> Tier 2, 3+ rows -> Tier 1. `hasProviders` mirrors that function's `!empty` override —
// a KPP with zero enabled providers is forced to Tier 4 even if the Typical Prices Chart
// aggregate (refreshed on its own schedule) still has buckets.
// NOTE: the doc writes most "[City]" placeholders bare (no state), but the two sentences it
// carried over verbatim from the opening paragraph ("Renting a dumpster in [City], [ST]..." /
// "Dumpster rental in [City], [ST] starts at...") keep the ", [ST]" from that source — matched
// here by using the full `city` there and a state-less `cityShort` everywhere else.
// SAFETY NET: if avg_extra_day_charge/avg_overweight_charge come back null/NaN, or a row's
// size_label doesn't parse to a number, we don't have Mark/product sign-off on fallback copy for
// individual sentences — so instead of risking "null"/"NaN"/"Infinity" in visible text AND in the
// FAQPage schema, the whole tier falls back to the generic, always-safe Tier 4 content.
const getKppFaqItems = (typicalPricesChart, hasProviders, city) => {
  const cityShort = city?.split(',')[0]?.trim() || city
  const rows = typicalPricesChart?.rows || []
  const bucketCount = hasProviders ? rows.length : 0

  if (bucketCount === 0) return buildTier4FaqItems(cityShort)

  const minPrice = fmtDollars(Math.min(...rows.map(r => r.low_price)))
  const maxPrice = fmtDollars(Math.max(...rows.map(r => r.high_price)))
  const minWeight = Math.min(...rows.map(r => r.min_weight))
  const maxWeight = Math.max(...rows.map(r => r.max_weight))
  const minDays = Math.min(...rows.map(r => r.min_days))
  const maxDays = Math.max(...rows.map(r => r.max_days))
  // Same fields the opening paragraph's disclaimer used to render (pre-PR #3932) before being
  // orphaned as dead code in typical-prices-chart.js — matched to Avg_Day_Cost/Avg_Weight_Cost
  // by the doc's own wording ("...per additional day" / "...per extra ton").
  const avgDayCost = fmtDollars(typicalPricesChart.avg_extra_day_charge)
  const avgWeightCost = fmtDollars(typicalPricesChart.avg_overweight_charge)

  const coreValuesValid = minPrice !== null && maxPrice !== null && avgDayCost !== null && avgWeightCost !== null &&
    Number.isFinite(minWeight) && Number.isFinite(maxWeight) && Number.isFinite(minDays) && Number.isFinite(maxDays)
  if (!coreValuesValid) return buildTier4FaqItems(cityShort)

  // Tier 1 — pricing in 3 or more buckets (small, medium, large, very large)
  if (bucketCount >= 3) {
    return [
      {
        question: `How much does it cost to rent a dumpster in ${cityShort}?`,
        answer: `The cost to rent a dumpster in ${cityShort} varies by size and weight allowance. On average, local prices range from ${minPrice} for smaller containers up to ${maxPrice} for larger containers.`
      },
      {
        question: `How long can I keep a dumpster in ${cityShort}?`,
        answer: `The typical rental period in ${cityShort} ranges from ${minDays} to ${maxDays} days. If your project takes longer, extension days are available for an average of ${avgDayCost} per additional day.`
      },
      {
        question: `What happens if I go over the dumpster's included weight?`,
        answer: `Every rental includes a baseline weight limit (typically ${minWeight} to ${maxWeight} tons depending on container size). If your load exceeds this limit, local waste processing plants charge overage fees. In ${cityShort}, overage fees average ${avgWeightCost} per extra ton.`
      },
      {
        question: `Do I need a city permit to rent a dumpster in ${cityShort}?`,
        answer: `If the dumpster sits entirely on private property (like your driveway or private job site), generally no permit is required. However, if the dumpster must be placed on a public street, alley, or sidewalk in ${cityShort}, a local municipal right-of-way permit may be necessary before delivery.`
      },
      {
        question: `Are there any hidden fees with dumpster rentals in ${cityShort}?`,
        answer: `Standard quotes generally cover delivery, pickup, rental duration, and an amount of included debris weight. Additional charges only apply if you exceed your included debris weight, keep the dumpster beyond the included rental period, require a public street permit, or dispose of prohibited materials.`
      },
      {
        question: `Which dumpster size is best for my project in ${cityShort}?`,
        answer: `Matching the right dumpster size to your job keeps costs low and avoids second-haul fees: Small (10-yard): Ideal for heavy debris (dirt/concrete), small bathroom remodels, or garage cleanouts. Medium (20-yard): Great for deck removals, carpet replacement, roof tear-offs, or multi-room cleanouts. Large (30-yard): Best for full home additions, major estate cleanouts, or large construction jobs. Very Large (40-yard): Designed for full commercial teardowns, major storm cleanups, or large-scale demolition.`
      },
      {
        question: `What items are prohibited in roll-off dumpsters in ${cityShort}?`,
        answer: `Hazardous materials (paints, solvents, oils), flammable liquids, asbestos, car batteries, propane tanks, and medical waste cannot be placed in dumpsters. Certain items like tires, appliances, or mattresses may be allowed but might incur extra handling fees from local landfills. Each service provider has their own list of disallowed items that can be referenced at the time you book the dumpster.`
      }
    ]
  }

  // Tier 2 — pricing in 2 buckets (2 rows on the chart)
  if (bucketCount === 2) {
    const sizes = rows.flatMap(r => parseSizeRange(r.size_label)).filter(n => n !== null)
    if (!sizes.length) return buildTier4FaqItems(cityShort)
    const minSize = Math.min(...sizes)
    const maxSize = Math.max(...sizes)
    return [
      {
        question: `How much does a dumpster rental cost in ${cityShort}?`,
        answer: `Renting a dumpster in ${city} typically costs from ${minPrice} to ${maxPrice}, depending on dumpster size, the amount of debris weight included (${minWeight} to ${maxWeight} tons), and rental period (${minDays} to ${maxDays} days).`
      },
      {
        question: `What dumpster sizes can I rent in ${cityShort}?`,
        answer: `Local haulers in ${cityShort} most commonly rent 10 to 40 cubic yard dumpsters. Hometown currently has pricing for ${minSize}-yard to ${maxSize}-yard containers. These dumpster sizes cover the vast majority of residential cleanouts, roofing jobs, and remodeling projects. To rent a different size, select "Get pricing & availability" from any service provider to get a quote.`
      },
      {
        question: `How long is the standard dumpster rental period in ${cityShort}?`,
        answer: `Most dumpster rentals in ${cityShort} include a standard rental period of ${minDays} to ${maxDays} days. If you need more time to complete your project, extra days can typically be added for around ${avgDayCost} per day.`
      },
      {
        question: `How do weight limits work for dumpsters in ${cityShort}?`,
        answer: `Your rental rate includes a specific tonnage allowance (typically ${minWeight} to ${maxWeight} tons). If your debris weighs more than the limit when hauled to the local transfer station, an overage fee averaging ${avgWeightCost} per ton will be added to the final bill.`
      },
      {
        question: `Do I need a permit to put a dumpster on the street in ${cityShort}?`,
        answer: `You do not need a permit if the dumpster is kept on private property, such as your driveway. If placement on a public roadway or sidewalk in ${cityShort} is required, check with the local city government or your rental provider regarding street permit requirements.`
      },
      {
        question: `What materials are restricted from roll-off dumpsters in ${cityShort}?`,
        answer: `Items such as hazardous chemicals, paint cans, motor oil, batteries, propane tanks, and biohazardous waste are strictly prohibited in roll-off dumpsters. Depending on the service provider, additional items may be prohibited to comply with local regulations and environmental laws.`
      }
    ]
  }

  // Tier 3 — pricing in 1 bucket (1 row on the chart)
  const [minSize] = parseSizeRange(rows[0].size_label)
  if (minSize === null) return buildTier4FaqItems(cityShort)
  const days = rows[0].min_days
  return [
    {
      question: `How much does it cost to rent a dumpster in ${cityShort}?`,
      answer: `Dumpster rental in ${city} starts at ${minPrice} for ${minSize} dumpsters, including ${minWeight} ton of debris weight and a ${days}-day rental. To check prices on dumpsters ranging from 10-yard to 40-yard, select "Get Pricing & Availability" on any provider below to check availability and compare quotes for all available container sizes in your area.`
    },
    {
      question: `How long can I keep a dumpster in ${cityShort}?`,
      answer: `Dumpster rentals in ${cityShort} generally come with an included number of days. 3 days to 10 days is the most common, but some service providers do offer a longer rental period. If your cleanup takes longer, rental extensions can usually be arranged with your service provider for a small daily fee (averaging ${avgDayCost}/day).`
    },
    {
      question: `How much weight is included with a dumpster rental in ${cityShort}?`,
      answer: `The amount of included weight generally increases as dumpster size increases and may be different for each service provider. Check the included weight allowance of your dumpster before booking. If your debris exceeds the included weight, extra weight fees in ${cityShort} average ${avgWeightCost} per extra ton.`
    },
    {
      question: `Where can I place a dumpster on my property in ${cityShort}?`,
      answer: `Place the dumpster on a hard, flat surface such as a concrete driveway or gravel parking pad. Make sure there is clear overhead clearance (away from low tree branches and power lines) and sufficient space for the delivery truck to maneuver. If you need to place the dumpster on a city street or public right away, a permit is generally required.`
    },
    {
      question: `What items cannot go into a dumpster in ${cityShort}?`,
      answer: `Standard landfill restrictions apply in ${cityShort}. Prohibited items include wet paint, chemicals, hazardous materials, liquids, motor oil, propane tanks, tires, and large electronics.`
    }
  ]
}

// FAQPage schema.org markup (https://schema.org/FAQPage) for the KPP FAQ section above —
// mainEntity text must stay byte-for-byte identical to what's rendered on the page.
const generateFaqSchema = (faqItems) => {
  if (!faqItems?.length) return null
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(item => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer
      }
    }))
  }
}

module.exports = { convertHTMLObjToArrayForKpp, generateJsonLdForKpp, generateTypicalPricesSchema, generateJsonLdForPpp, generateJsonLdForPppConstruction, formatBookingJson, manualGoogleEvents, formatData, getFormDataForStorage, getInitialFormValues, trackProCardsViews, trackSimpleEvent, trackBookingSubmission, createAddressEvent, trackProCardsViewsOnViewport, buildBreadcrumbSchema, buildWasteManagementServiceSchema, generateJsonLdForHome, parseSizeRange, getKppFaqItems, generateFaqSchema };
